import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import ndsTokens from 'nicollet-tokens';
import { slugify, ndsFormStyles } from '@nicollet/common';
import { SelectOuterWrap, SelectWrap, SelectLabel, SelectInput, SelectVisual } from './styles.js';
import { Microcopy } from '../Microcopy/index.js';
import { ErrorMessage } from '../ErrorMessage/index.js';

const SELECT_ICON_WIDTH = 14;

const SelectIcon = ({
  disabled,
  valid
}) => {
  let fillColor = ndsTokens.colors.text.onLight.primary;

  if (disabled) {
    fillColor = ndsTokens.colors.interactive.disabled.default;
  } else if (valid === false) {
    fillColor = ndsFormStyles.colors.alert.warning.text;
  } // The 1px nudge on this is because the downward-facing chevron is visually top-heavy and needs a
  // little offset to look right.


  return jsx("svg", Object.assign({
    height: SELECT_ICON_WIDTH,
    style: {
      display: 'block',
      position: 'relative',
      top: '1px'
    },
    tabIndex: -1,
    viewBox: "0 0 24 24",
    width: SELECT_ICON_WIDTH,
    xmlns: "http://www.w3.org/2000/svg"
  }, {
    children: jsx("path", {
      d: "M12.002 15.38l9.589-9.588 1.414 1.414-11.003 11.002L1 7.206l1.414-1.414z",
      fill: fillColor,
      fillRule: "nonzero"
    }, void 0)
  }), void 0);
};

SelectIcon.displayName = 'SelectIcon';
const Select = forwardRef(({
  autoFocus,
  className,
  dataAttributes = {},
  'data-test': testId,
  disabled,
  disableLiveRegion,
  display = 'inline-flex',
  errorText,
  hasFocus,
  hasHover,
  hideLabel,
  id,
  isValid,
  label = '',
  microcopy,
  name,
  onBlur,
  onChange,
  options,
  prefixLabel,
  required,
  screenReader,
  elementSize = 'md',
  uncontrolled = false,
  value,
  variant,
  width
}, ref) => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const idValue = id || slugify(label) || 'default-id-change-me';

  if (process.env.NODE_ENV === 'development' && (!id || id === '')) {
    // eslint-disable-next-line no-console
    console.warn("Warning: No 'id' prop provided for Select. This will become a required prop for Select in the upcoming Nicollet v8 release.");
  }

  const htmlAttributes = {
    name,
    disabled,
    required,
    autoFocus,
    onBlur,
    onChange
  };

  if (testId) {
    dataAttributes.test = testId;
  }

  Object.keys(dataAttributes || {}).forEach(key => {
    htmlAttributes[`data-${key}`] = dataAttributes[key];
  });
  const compositeLabel = prefixLabel ? prefixLabel + label : label;
  const isLabelSelected = !uncontrolled && !value;
  return jsxs(SelectOuterWrap, Object.assign({
    className: className
  }, {
    children: [jsxs(SelectWrap, Object.assign({
      display: display,
      hasHover: hasHover,
      isValid: isValid,
      variant: variant
    }, {
      children: [jsx(SelectLabel, {
        animated: true,
        disabled: disabled,
        disableLiveRegion: disableLiveRegion,
        elementSize: elementSize,
        errorText: errorText,
        hidden: isLabelSelected || hideLabel,
        htmlFor: idValue,
        isFocused: !isLabelSelected,
        label: compositeLabel,
        microcopy: microcopy,
        screenReader: screenReader,
        valid: isValid
      }, void 0), jsxs(SelectInput, Object.assign({}, htmlAttributes, {
        defaultValue: uncontrolled ? value : undefined,
        elementSize: elementSize,
        id: idValue,
        ref: ref,
        value: uncontrolled ? undefined : value,
        width: width
      }, {
        children: [jsx("option", Object.assign({
          disabled: true,
          value: ""
        }, {
          children: compositeLabel
        }), void 0), options.map(({
          value: optionValue,
          label: optionLabel,
          disabled: optionDisabled,
          childOptions
        }) => childOptions ? jsx("optgroup", Object.assign({
          disabled: optionDisabled,
          label: `${optionLabel}`
        }, {
          children: childOptions.map(({
            value: childValue,
            label: childLabel,
            disabled: childDisabled
          }) => jsx("option", Object.assign({
            disabled: childDisabled,
            value: childValue
          }, {
            children: childLabel
          }), `${childLabel}-${childValue}`))
        }), `${optionLabel}`) : jsx("option", Object.assign({
          disabled: optionDisabled,
          value: optionValue
        }, {
          children: optionLabel
        }), `${optionLabel}-${optionValue}`))]
      }), void 0), jsx(SelectVisual, Object.assign({
        hasFocus: hasFocus,
        isValid: isValid,
        variant: variant
      }, {
        children: jsx(SelectIcon, {
          disabled: disabled,
          valid: isValid
        }, void 0)
      }), void 0)]
    }), void 0), isValid === false && errorText && jsx(ErrorMessage, Object.assign({
      "aria-hidden": "true"
    }, {
      children: errorText
    }), void 0), microcopy && jsx(Microcopy, Object.assign({
      "aria-hidden": "true"
    }, {
      children: microcopy
    }), void 0)]
  }), void 0);
});
Select.displayName = 'Select';

export { Select };
